<template>
  <div class="min-h-screen bg-gray-900 text-white">
    <!-- Header -->
    <header class="bg-gray-800 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-white">CallBlasterAI</h1>
          </div>
          <div class="text-sm text-gray-400">
            Sistema de llamadas inteligentes
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Welcome Section -->
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold text-white mb-4">🎉 ¡CallBlasterAI MVP Funcionando!</h2>
        <p class="text-gray-300 text-lg">Sistema de comunicación de voz en tiempo real con IA</p>
        <div class="mt-4 p-4 bg-green-800 rounded-lg">
          <p class="text-green-200">✅ Frontend: Completamente funcional</p>
          <p class="text-green-200">✅ Backend: APIs respondiendo correctamente</p>
          <p class="text-green-200">✅ LiveKit: Configurado para llamadas</p>
        </div>
      </div>

      <!-- Estado de Llamada Activa -->
      <div v-if="isInCall || isConnecting" class="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="bg-green-500 rounded-full p-2">
              <svg v-if="!isConnecting" class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
              </svg>
              <div v-else class="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-green-800">
                {{ isConnecting ? '🔄 Conectando...' : `📞 Llamada ${callType === 'phone' ? 'Telefónica' : 'Web'} Activa` }}
              </h3>
              <p class="text-green-600">
                {{ isConnecting ? 'Estableciendo conexión' : `${callType === 'phone' ? phoneNumber : currentRoom} - Duración: ${callDuration}` }}
              </p>
              <div v-if="participants.length > 0" class="text-sm text-green-500 mt-1">
                👥 {{ participants.length }} participante{{ participants.length > 1 ? 's' : '' }} conectado{{ participants.length > 1 ? 's' : '' }}
              </div>
            </div>
          </div>
          <div class="flex space-x-2">
            <button 
              v-if="isInCall && !isConnecting"
              @click="toggleMute" 
              :class="[
                'px-4 py-2 rounded-lg transition-colors',
                isMuted ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
              ]"
            >
              {{ isMuted ? '🔇 Silenciado' : '🎤 Activo' }}
            </button>
            <button 
              @click="endCall" 
              :disabled="isConnecting"
              class="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
            >
              📞 {{ isConnecting ? 'Cancelar' : 'Colgar' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Step 1: User Configuration -->
      <div class="bg-gray-800 rounded-lg p-6 mb-8 border-l-4 border-purple-500">
        <div class="flex items-center mb-4">
          <div class="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center mr-3 text-white font-bold">1</div>
          <h3 class="text-xl font-semibold">Configuración de Usuario</h3>
        </div>
        <p class="text-gray-400 mb-4">Primero, configura tu nombre para identificarte en las llamadas</p>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Tu nombre</label>
            <input 
              v-model="userName"
              type="text"
              placeholder="Ingresa tu nombre"
              class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
            />
          </div>
          <button 
            @click="setUser"
            :disabled="!userName"
            class="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            ✅ Guardar Nombre
          </button>
          <div v-if="userConfigured" class="text-green-400 text-sm">
            ✅ Usuario configurado: {{ userName }}
          </div>
        </div>
      </div>

      <!-- Step 2: Choose Call Type -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <!-- Phone Call -->
        <div class="bg-gray-800 rounded-lg p-6 border-l-4 border-green-500">
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3 text-white font-bold">2A</div>
            <h3 class="text-xl font-semibold flex items-center">
              📞 Llamada Telefónica
            </h3>
          </div>
          <p class="text-gray-400 mb-4">Llama directamente a un número de teléfono usando nuestro trunk SIP</p>
          <div class="space-y-3">
            <input 
              v-model="phoneNumber"
              type="tel"
              placeholder="Ej: +52 1 55 1234 5678"
              class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
            />
            <button 
              @click="makePhoneCall"
              :disabled="!userConfigured || !phoneNumber"
              class="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              📞 Llamar Ahora
            </button>
          </div>
        </div>

        <!-- Create Web Call -->
        <div class="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500">
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3 text-white font-bold">2B</div>
            <h3 class="text-xl font-semibold flex items-center">
              🎥 Crear Sala Web
            </h3>
          </div>
          <p class="text-gray-400 mb-4">Crea una nueva sala de llamada web y comparte el enlace</p>
          <button 
            @click="createNewCall"
            :disabled="!userConfigured"
            class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
          >
            🎥 Crear Sala
          </button>
        </div>

        <!-- Join Web Call -->
        <div class="bg-gray-800 rounded-lg p-6 border-l-4 border-orange-500">
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-3 text-white font-bold">2C</div>
            <h3 class="text-xl font-semibold flex items-center">
              🔗 Unirse a Sala
            </h3>
          </div>
          <p class="text-gray-400 mb-4">Únete a una sala existente con el código</p>
          <div class="space-y-3">
            <input 
              v-model="roomCode"
              type="text"
              placeholder="Código de sala"
              class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
              @keyup.enter="joinCall"
            />
            <button 
              @click="joinCall"
              :disabled="!userConfigured || !roomCode"
              class="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              🔗 Unirse
            </button>
          </div>
        </div>
      </div>

      <!-- API Test Section -->
      <div class="bg-gray-800 rounded-lg p-6 mb-8">
        <h3 class="text-xl font-semibold mb-4">Pruebas de API</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            @click="testTokenAPI"
            class="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Probar API Token
          </button>
          <button 
            @click="testChatAPI"
            class="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Probar API Chat
          </button>
        </div>
        <div v-if="apiResult" class="mt-4 p-3 bg-gray-700 rounded text-sm">
          <strong>Resultado:</strong> {{ apiResult }}
        </div>
      </div>

      <!-- Features -->
      <div class="bg-gray-800 rounded-lg p-6">
        <h3 class="text-xl font-semibold mb-4">Características</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="text-center">
            <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 class="font-semibold mb-1">Llamadas Web</h4>
            <p class="text-sm text-gray-400">Comunicación de voz en tiempo real</p>
          </div>
          <div class="text-center">
            <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h4 class="font-semibold mb-1">Llamadas Telefónicas</h4>
            <p class="text-sm text-gray-400">Integración con números de teléfono</p>
          </div>
          <div class="text-center">
            <div class="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 class="font-semibold mb-1">IA Inteligente</h4>
            <p class="text-sm text-gray-400">Asistente de voz con IA</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import LiveKitService from './services/livekit.js'
import { requestMicrophonePermission, getWebRTCInfo } from './utils/webrtc.js'

// Estados principales
const userName = ref('')
const roomCode = ref('')
const phoneNumber = ref('')
const apiResult = ref('')
const userConfigured = ref(false)

// Estados de llamada
const isInCall = ref(false)
const callType = ref('') // 'phone' o 'web'
const currentRoom = ref('')
const callDuration = ref('00:00')
const isMuted = ref(false)
const isConnecting = ref(false)
const participants = ref([])
let callStartTime = null
let callTimer = null
let liveKitService = null

onMounted(() => {
  console.log('✅ CallBlasterAI cargado correctamente')
  
  // Verificar estado de WebRTC
  const webrtcInfo = getWebRTCInfo()
  console.log('🌐 Estado WebRTC:', webrtcInfo)
  
  if (!webrtcInfo.isAvailable) {
    console.warn('⚠️ WebRTC no disponible')
  }
  
  if (!webrtcInfo.isSecure && webrtcInfo.hostname !== 'localhost') {
    console.warn('⚠️ Protocolo no seguro, WebRTC puede no funcionar correctamente')
  }
  
  // Inicializar LiveKit service con store methods
  liveKitService = new LiveKitService({
    setConnecting: (connecting) => { isConnecting.value = connecting },
    setConnected: (connected) => { isInCall.value = connected },
    setError: (error) => { apiResult.value = error || '' },
    setMuted: (muted) => { isMuted.value = muted },
    addParticipant: (participant) => { participants.value.push(participant) },
    removeParticipant: (participantId) => {
      participants.value = participants.value.filter(p => p.id !== participantId)
    },
    updateParticipant: (participantId, updates) => {
      const participant = participants.value.find(p => p.id === participantId)
      if (participant) {
        Object.assign(participant, updates)
      }
    },
    reset: () => {
      participants.value = []
      isInCall.value = false
      isConnecting.value = false
      isMuted.value = false
    }
  })
  
  // Cargar configuración guardada
  const savedUser = localStorage.getItem('callblaster-user')
  if (savedUser) {
    userName.value = savedUser
    userConfigured.value = true
  }
})

onUnmounted(() => {
  if (callTimer) {
    clearInterval(callTimer)
  }
})

// Función para actualizar duración de llamada
const updateCallDuration = () => {
  if (!callStartTime) return
  
  const now = new Date()
  const diff = now - callStartTime
  const minutes = Math.floor(diff / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)
  callDuration.value = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

// Función auxiliar para solicitar permisos de micrófono
const requestMicPermission = async () => {
  apiResult.value = '🎤 Verificando permisos de micrófono...'
  
  const result = await requestMicrophonePermission()
  
  if (result.success) {
    apiResult.value = '✅ ' + result.message
    return true
  } else {
    apiResult.value = (result.canContinue ? '⚠️ ' : '❌ ') + result.message
    
    if (!result.canContinue) {
      throw new Error(result.message)
    }
    
    // Continuar sin permisos en modo desarrollo
    console.warn('Continuando sin permisos de micrófono')
    return result.canContinue
  }
}

const setUser = () => {
  if (userName.value.trim()) {
    userConfigured.value = true
    localStorage.setItem('callblaster-user', userName.value.trim())
    // Ya no mostrar alert, solo confirmar visualmente
  }
}

const makePhoneCall = async () => {
  if (!userConfigured.value) {
    alert('⚠️ Por favor configura tu nombre primero (Paso 1)')
    return
  }
  
  if (!phoneNumber.value.trim()) {
    alert('⚠️ Por favor ingresa un número de teléfono')
    return
  }
  
  // Validar formato de número mexicano (10 dígitos)
  const cleanNumber = phoneNumber.value.trim().replace(/\D/g, '')
  if (cleanNumber.length !== 10) {
    alert('⚠️ Por favor ingresa un número válido de 10 dígitos')
    return
  }
  
  try {
    // Verificar permisos de micrófono
    const micPermission = await requestMicPermission()
    const webrtcInfo = getWebRTCInfo()
    
    // Llamar a la API para iniciar llamada telefónica
    apiResult.value = '📞 Iniciando llamada telefónica...'
    
    // Para LiveKit, usar formato internacional si es necesario
    const phoneForLiveKit = `+521${cleanNumber}` // Formato mexicano internacional
    
    const response = await fetch('/api/phone-call', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        phoneNumber: phoneForLiveKit,
        callerName: userName.value.trim(),
        webrtcAvailable: webrtcInfo.isAvailable
      })
    })

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json()
    
    if (data.success) {
      // Generar token para la sala de la llamada telefónica
      const tokenResponse = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roomName: data.roomName,
          participantName: userName.value.trim(),
          metadata: { callType: 'phone', phoneNumber: phoneForLiveKit }
        })
      })
      
      const tokenData = await tokenResponse.json()
      
      if (tokenData.token) {
        // Conectar a LiveKit para la llamada telefónica
        await liveKitService.connectToRoom(tokenData.wsUrl, tokenData.token)
        
        // Solo habilitar micrófono si WebRTC está disponible
        if (webrtcInfo.isAvailable && micPermission) {
          try {
            const room = liveKitService.getCurrentRoom()
            await room.localParticipant.enableCameraAndMicrophone()
            console.log('✅ Micrófono habilitado para la llamada')
          } catch (micError) {
            console.warn('⚠️ No se pudo habilitar el micrófono:', micError.message)
            // Continuar sin micrófono en modo desarrollo
          }
        } else {
          console.log('ℹ️ Llamada iniciada sin micrófono (modo desarrollo)')
        }
        
        // Configurar estado de llamada
        callType.value = 'phone'
        currentRoom.value = data.roomName
        callStartTime = new Date()
        callTimer = setInterval(updateCallDuration, 1000)
        
        apiResult.value = `✅ Llamada telefónica iniciada a ${cleanNumber}`
      } else {
        apiResult.value = `❌ Error obteniendo token: ${tokenData.error}`
      }
    } else {
      apiResult.value = `❌ Error al iniciar llamada: ${data.error}`
    }
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      apiResult.value = '❌ Se requieren permisos de micrófono para realizar llamadas'
    } else {
      apiResult.value = `❌ Error de conexión: ${error.message}`
    }
    console.error('Error en makePhoneCall:', error)
  }
}

const createNewCall = async () => {
  if (!userConfigured.value) {
    alert('⚠️ Por favor configura tu nombre primero (Paso 1)')
    return
  }
  
  try {
    // Verificar permisos de micrófono
    const micPermission = await requestMicPermission()
    const webrtcInfo = getWebRTCInfo()
    
    const roomName = `room-${Date.now()}`
    
    // Generar token para la sala
    const response = await fetch('/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        roomName: roomName,
        participantName: userName.value.trim(),
        metadata: { callType: 'web' }
      })
    })
    
    const data = await response.json()
    
    if (data.token) {
      // Conectar a LiveKit
      await liveKitService.connectToRoom(data.wsUrl, data.token)
      
      // Solo habilitar micrófono si WebRTC está disponible
      if (webrtcInfo.isAvailable && micPermission) {
        try {
          const room = liveKitService.getCurrentRoom()
          await room.localParticipant.enableCameraAndMicrophone()
          console.log('✅ Micrófono habilitado para la sala web')
        } catch (micError) {
          console.warn('⚠️ No se pudo habilitar el micrófono:', micError.message)
          // Continuar sin micrófono en modo desarrollo
        }
      } else {
        console.log('ℹ️ Sala web creada sin micrófono (modo desarrollo)')
      }
      
      // Configurar estado de llamada
      callType.value = 'web'
      currentRoom.value = roomName
      callStartTime = new Date()
      callTimer = setInterval(updateCallDuration, 1000)
      
      apiResult.value = `✅ Sala creada: ${roomName}. Comparte este código para que otros se unan.`
    } else {
      apiResult.value = `❌ Error al crear sala: ${data.error}`
    }
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      apiResult.value = '❌ Se requieren permisos de micrófono para realizar llamadas'
    } else {
      apiResult.value = `❌ Error: ${error.message}`
    }
    console.error('Error en createNewCall:', error)
  }
}

const joinCall = async () => {
  if (!userConfigured.value) {
    alert('⚠️ Por favor configura tu nombre primero (Paso 1)')
    return
  }
  
  if (!roomCode.value.trim()) {
    alert('⚠️ Por favor ingresa el código de sala')
    return
  }
  
  try {
    // Verificar permisos de micrófono
    const micPermission = await requestMicPermission()
    const webrtcInfo = getWebRTCInfo()
    
    // Generar token para unirse a la sala
    const response = await fetch('/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        roomName: roomCode.value.trim(),
        participantName: userName.value.trim(),
        metadata: { callType: 'web', joining: true }
      })
    })
    
    const data = await response.json()
    
    if (data.token) {
      // Conectar a LiveKit
      await liveKitService.connectToRoom(data.wsUrl, data.token)
      
      // Solo habilitar micrófono si WebRTC está disponible
      if (webrtcInfo.isAvailable && micPermission) {
        try {
          const room = liveKitService.getCurrentRoom()
          await room.localParticipant.enableCameraAndMicrophone()
          console.log('✅ Micrófono habilitado para unirse a la sala')
        } catch (micError) {
          console.warn('⚠️ No se pudo habilitar el micrófono:', micError.message)
          // Continuar sin micrófono en modo desarrollo
        }
      } else {
        console.log('ℹ️ Conectado a sala sin micrófono (modo desarrollo)')
      }
      
      // Configurar estado de llamada
      callType.value = 'web'
      currentRoom.value = roomCode.value.trim()
      callStartTime = new Date()
      callTimer = setInterval(updateCallDuration, 1000)
      
      apiResult.value = `✅ Conectado a sala: ${roomCode.value}`
    } else {
      apiResult.value = `❌ Error al unirse a la sala: ${data.error}`
    }
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      apiResult.value = '❌ Se requieren permisos de micrófono para realizar llamadas'
    } else {
      apiResult.value = `❌ Error: ${error.message}`
    }
    console.error('Error en joinCall:', error)
  }
}

const toggleMute = async () => {
  try {
    if (liveKitService && liveKitService.isConnected()) {
      const newMutedState = !isMuted.value
      await liveKitService.toggleMute(newMutedState)
      apiResult.value = newMutedState ? '🔇 Micrófono silenciado' : '🎤 Micrófono activado'
    }
  } catch (error) {
    console.error('Error cambiando estado del micrófono:', error)
    apiResult.value = '❌ Error al cambiar estado del micrófono'
  }
}

const endCall = async () => {
  try {
    // Desconectar de LiveKit
    if (liveKitService && liveKitService.isConnected()) {
      await liveKitService.disconnect()
    }
    
    // Limpiar estado
    isInCall.value = false
    callType.value = ''
    currentRoom.value = ''
    callDuration.value = '00:00'
    isMuted.value = false
    participants.value = []
    callStartTime = null
    
    if (callTimer) {
      clearInterval(callTimer)
      callTimer = null
    }
    
    apiResult.value = '✅ Llamada terminada'
  } catch (error) {
    console.error('Error terminando llamada:', error)
    apiResult.value = '❌ Error al terminar llamada'
  }
}

const testTokenAPI = async () => {
  try {
    const response = await fetch('/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        roomName: 'test-room',
        participantName: 'test-user'
      })
    })
    const data = await response.json()
    apiResult.value = `✅ Token API: ${data.token ? 'Token generado correctamente' : 'Error'}`
  } catch (error) {
    apiResult.value = `❌ Token API: ${error.message}`
  }
}

const testChatAPI = async () => {
  try {
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: 'Hola, ¿cómo estás?'
      })
    })
    const data = await response.json()
    apiResult.value = `✅ Chat API: ${data.response ? 'IA respondió correctamente' : 'Error'}`
  } catch (error) {
    apiResult.value = `❌ Chat API: ${error.message}`
  }
}
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
}
</style>

<style>
/* Estilos globales y de transición que sabemos que son seguros */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar personalizado */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
